<template>

    <div class="gn-page">

        <!-- 1. En-tête -->
        <div class="ds-page-head">
            <div>
                <h1 class="ds-page-head__title">Vue d'ensemble</h1>
                <p class="ds-page-head__sub">
                    {{ dateDuJour }}<template v-if="derniereMaj"> · données de {{ derniereMaj }}</template>
                </p>
            </div>
            <button type="button" class="gn-btn" :disabled="nbChargements > 0" @click="toutCharger(true)">
                <i class="ri-refresh-line" :class="{ 'gn-spin': nbChargements > 0 }"></i> Actualiser
            </button>
        </div>

        <!-- Données servies par le cache 5 min -->
        <bandeaucachetb v-if="depuisCache" quoi="les indicateurs de la vue d'ensemble" verbe="sont gardés"
                        manque="Les ventes, achats et mouvements de stock faits depuis n'y figurent pas encore."
                        :heure="heureCache" @actualiser="toutCharger(true)"></bandeaucachetb>

        <!-- 2. Alertes -->
        <div v-if="alertes.length" class="gn-alerts">
            <button v-for="(a, i) in alertes" :key="i" type="button" class="gn-alert" :class="'gn-alert--' + a.niveau" @click="aller(a.cible, a.onglet)">
                <i :class="a.icone"></i><span v-html="a.texte"></span>
            </button>
        </div>

        <!-- 3. KPI -->
        <div class="gn-kpis">
            <template v-if="ca.charge">
                <div class="gn-kpi gn-kpi--main">
                    <div class="gn-kpi__lbl">CA du mois ({{ nomMois(moisCourant) }}) <i class="ri-shopping-bag-3-line"></i></div>
                    <div class="gn-kpi__val">{{ formatMoney(kpiCa.mois) }} <small>F CFA</small></div>
                    <div class="gn-kpi__sub">
                        <span v-if="kpiCa.moisPrec" :class="kpiCa.mois >= kpiCa.moisPrec ? 'gn-up--light' : 'gn-down--light'">{{ fleche(kpiCa.mois, kpiCa.moisPrec) }}</span>
                        <template v-if="kpiCa.moisPrec"> vs {{ nomMois((moisCourant + 11) % 12) }} (mois en cours)</template>
                        <template v-else>mois en cours</template>
                    </div>
                    <svg v-if="sparkline" class="gn-spark" width="110" height="36" aria-hidden="true">
                        <polyline fill="none" stroke="#fff" stroke-width="2" :points="sparkline"></polyline>
                    </svg>
                </div>
                <div class="gn-kpi">
                    <div class="gn-kpi__lbl">CA de l'année <i class="ri-calendar-check-line gn-i-vio"></i></div>
                    <div class="gn-kpi__val">{{ formatMoney(kpiCa.annee) }} <small>F</small></div>
                    <div class="gn-kpi__sub">
                        <template v-if="kpiCa.anneePrec">
                            <span :class="kpiCa.annee >= kpiCa.anneePrec ? 'gn-up' : 'gn-down'">{{ fleche(kpiCa.annee, kpiCa.anneePrec) }}</span>
                            vs même période {{ anneeCourante - 1 }}
                        </template>
                        <template v-else>janvier à {{ nomMois(moisCourant) }} {{ anneeCourante }}</template>
                    </div>
                </div>
            </template>
            <template v-else>
                <div v-for="n in 2" :key="'s' + n" class="gn-kpi"><div class="gn-skel" style="width:50%"></div><div class="gn-skel gn-skel--lg"></div><div class="gn-skel" style="width:80%"></div></div>
            </template>

            <div class="gn-kpi" role="button" tabindex="0" @click="aller('tiers')">
                <div class="gn-kpi__lbl">Créances clients <i class="ri-hand-coin-line gn-i-blue"></i></div>
                <template v-if="tiers.clt.charge">
                    <div class="gn-kpi__val">{{ formatMoney(totauxTiers.clt.reste) }} <small>F</small></div>
                    <div class="gn-kpi__sub">{{ totauxTiers.clt.nb }} client{{ totauxTiers.clt.nb > 1 ? 's' : '' }} · {{ pourcent(totauxTiers.clt.reste, totauxTiers.clt.montant) }} % du CA facturé ({{ libellePeriodeTiers(tiers.clt.periode) }})</div>
                </template>
                <template v-else><div class="gn-skel gn-skel--lg"></div><div class="gn-skel" style="width:80%"></div></template>
            </div>
            <div class="gn-kpi" role="button" tabindex="0" @click="aller('tiers')">
                <div class="gn-kpi__lbl">Dettes fournisseurs <i class="ri-bank-card-line gn-i-red"></i></div>
                <template v-if="tiers.frs.charge">
                    <div class="gn-kpi__val">{{ formatMoney(totauxTiers.frs.reste) }} <small>F</small></div>
                    <div class="gn-kpi__sub">{{ totauxTiers.frs.nb }} fournisseur{{ totauxTiers.frs.nb > 1 ? 's' : '' }} à régler ({{ libellePeriodeTiers(tiers.frs.periode) }})</div>
                </template>
                <template v-else><div class="gn-skel gn-skel--lg"></div><div class="gn-skel" style="width:80%"></div></template>
            </div>
            <div class="gn-kpi" role="button" tabindex="0" @click="aller('stock', 'rup')">
                <div class="gn-kpi__lbl">Stock à surveiller <i class="ri-archive-line gn-i-amb"></i></div>
                <template v-if="stock.charge">
                    <div class="gn-kpi__val">{{ formatNombre(stock.ruptures.length + stock.sousmin.length) }} <small>articles</small></div>
                    <div class="gn-kpi__sub"><span class="gn-down">{{ formatNombre(stock.ruptures.length) }} ruptures</span> · {{ formatNombre(stock.sousmin.length) }} sous le minimum</div>
                </template>
                <template v-else><div class="gn-skel gn-skel--lg"></div><div class="gn-skel" style="width:80%"></div></template>
            </div>
        </div>

        <!-- 4. CA mensuel + top articles -->
        <div class="gn-grid2">
            <div class="gn-card">
                <div class="gn-card__head">
                    <h2><i class="ri-line-chart-line"></i> Chiffre d'affaires mensuel <span>· ventes moins retours</span></h2>
                    <div class="gn-tools">
                        <div class="gn-seg" role="group">
                            <button type="button" :class="{ 'gn-seg--on': vueCa === 'total' }" @click="vueCa = 'total'">Total</button>
                            <button type="button" :class="{ 'gn-seg--on': vueCa === 'mag' }" @click="vueCa = 'mag'">Par magasin</button>
                        </div>
                        <div v-if="vueCa === 'total'" class="gn-seg" role="group">
                            <button type="button" :class="{ 'gn-seg--on': nbAnnees === 1 }" @click="nbAnnees = 1">Cette année</button>
                            <button type="button" :class="{ 'gn-seg--on': nbAnnees === 2 }" @click="nbAnnees = 2">vs N-1</button>
                            <button type="button" :class="{ 'gn-seg--on': nbAnnees === 3 }" @click="nbAnnees = 3">3 ans</button>
                        </div>
                    </div>
                </div>
                <div class="gn-card__body">
                    <template v-if="vueCa === 'total'">
                        <apexcharts v-if="ca.charge && seriesCa.length" type="line" height="300" :options="optionsCa" :series="seriesCa"></apexcharts>
                        <div v-else-if="ca.charge" class="gn-empty"><i class="ri-line-chart-line"></i>Aucune vente enregistrée.</div>
                        <div v-else class="gn-skel gn-skel--chart"></div>
                    </template>
                    <template v-else>
                        <apexcharts v-if="caMag.charge && caMag.series.length" type="bar" height="300" :options="optionsCaMag" :series="caMag.series"></apexcharts>
                        <div v-else-if="caMag.charge" class="gn-empty"><i class="ri-store-2-line"></i>Aucune vente cette année.</div>
                        <div v-else class="gn-skel gn-skel--chart"></div>
                    </template>
                </div>
            </div>

            <div class="gn-card">
                <div class="gn-card__head">
                    <h2><i class="ri-trophy-line"></i> Top articles <span>· {{ anneeCourante }}, en CA</span></h2>
                    <select v-if="top.magasins.length > 1" class="gn-input" v-model="top.magasin" aria-label="Magasin">
                        <option value="">Tous les magasins</option>
                        <option v-for="m in top.magasins" :key="m" :value="m">{{ m }}</option>
                    </select>
                </div>
                <div class="gn-card__body">
                    <template v-if="top.charge">
                        <div v-if="!topArticles.length" class="gn-empty"><i class="ri-trophy-line"></i>Aucune vente cette année.</div>
                        <div v-else class="gn-rank">
                            <div v-for="(t, i) in topArticles" :key="t.nom" class="gn-rank__row">
                                <span class="gn-rank__n">{{ i + 1 }}</span>
                                <div class="gn-min0">
                                    <div class="gn-rank__name" :title="t.nom">{{ t.nom }}</div>
                                    <div class="gn-rank__bar"><i :style="{ width: (t.ca / topArticles[0].ca * 100) + '%' }"></i></div>
                                </div>
                                <div class="gn-rank__val">{{ formatMoney(t.ca) }} F<small>{{ pourcent(t.ca, kpiCa.annee) }} % du CA</small></div>
                            </div>
                        </div>
                    </template>
                    <template v-else><div v-for="n in 6" :key="n" class="gn-skel"></div></template>
                </div>
            </div>
        </div>

        <!-- 5. Créances / dettes -->
        <div ref="tiers" class="gn-grid2 gn-grid2--eq">
            <div v-for="cfg in configTiers" :key="cfg.cle" class="gn-card">
                <div class="gn-card__head">
                    <h2><i :class="cfg.icone"></i> {{ cfg.titre }} <span v-if="tiers[cfg.cle].charge">· {{ totauxTiers[cfg.cle].nb }} à régler</span></h2>
                    <div class="gn-tools">
                        <div class="gn-seg" role="group">
                            <button v-for="p in periodesTiers" :key="p.code" type="button"
                                    :class="{ 'gn-seg--on': tiers[cfg.cle].periode === p.code }"
                                    @click="tiers[cfg.cle].periode = p.code; chargerTiers(cfg.cle)">{{ p.label }}</button>
                        </div>
                        <div class="gn-msearch">
                            <i class="ri-search-line"></i>
                            <input type="search" v-model="tiers[cfg.cle].recherche" placeholder="Rechercher...">
                        </div>
                    </div>
                </div>
                <div class="gn-tiers">
                    <table v-if="tiers[cfg.cle].charge">
                        <thead>
                        <tr>
                            <th class="th gn-th--click" @click="tiers[cfg.cle].tri = 'nom'">{{ cfg.colNom }}{{ tiers[cfg.cle].tri === 'nom' ? ' ↓' : '' }}</th>
                            <th class="th th--right gn-th--click" @click="tiers[cfg.cle].tri = 'montant'">{{ cfg.colMontant }}{{ tiers[cfg.cle].tri === 'montant' ? ' ↓' : '' }}</th>
                            <th class="th th--right gn-th--click" @click="tiers[cfg.cle].tri = 'reste'">{{ cfg.colReste }}{{ tiers[cfg.cle].tri === 'reste' ? ' ↓' : '' }}</th>
                            <th class="th th--right gn-hide-sm">Payé</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-if="!lignesTiers[cfg.cle].length">
                            <td colspan="4"><div class="gn-empty"><i class="ri-user-search-line"></i>{{ tiers[cfg.cle].recherche ? 'Aucun tiers trouvé.' : cfg.vide }}</div></td>
                        </tr>
                        <tr v-for="t in lignesTiers[cfg.cle]" :key="t.nom" class="gn-row">
                            <td class="td">
                                <div class="gn-who">
                                    <span class="gn-av" :style="{ background: couleur(t.nom) }">{{ initiales(t.nom) }}</span>
                                    <b>{{ t.nom }}</b>
                                </div>
                            </td>
                            <td class="td td--right">{{ formatMoney(t.montant) }}</td>
                            <td class="td td--right gn-strong" :style="{ color: t.reste > 0 ? cfg.couleur : '#94a3b8' }">{{ t.reste > 0 ? formatMoney(t.reste) : 'Soldé' }}</td>
                            <td class="td td--right gn-hide-sm">
                                <span class="gn-pct">{{ Math.round(t.paye) }} %<span><i :style="{ width: Math.max(0, Math.min(100, t.paye)) + '%', background: t.paye >= 90 ? '#059669' : t.paye >= 60 ? '#d97706' : '#dc2626' }"></i></span></span>
                            </td>
                        </tr>
                        </tbody>
                        <tfoot>
                        <tr>
                            <td class="td">Total</td>
                            <td class="td td--right">{{ formatMoney(totauxTiers[cfg.cle].montantFiltre) }}</td>
                            <td class="td td--right">{{ formatMoney(totauxTiers[cfg.cle].resteFiltre) }}</td>
                            <td class="td td--right gn-hide-sm">{{ 100 - pourcent(totauxTiers[cfg.cle].resteFiltre, totauxTiers[cfg.cle].montantFiltre) }} %</td>
                        </tr>
                        </tfoot>
                    </table>
                    <div v-else class="gn-card__body"><div v-for="n in 5" :key="n" class="gn-skel"></div></div>
                </div>
            </div>
        </div>

        <!-- 6. Stock + bénéfice -->
        <div ref="stock" class="gn-grid2 gn-grid2--eq">
            <div class="gn-card">
                <div class="gn-card__head">
                    <h2><i class="ri-archive-line"></i> Alertes de stock</h2>
                    <div class="gn-tools">
                        <select v-if="magasinsStock.length > 1" class="gn-input" v-model="stock.magasin" aria-label="Magasin">
                            <option value="">Tous les magasins</option>
                            <option v-for="m in magasinsStock" :key="m" :value="m">{{ m }}</option>
                        </select>
                        <div class="gn-msearch">
                            <i class="ri-search-line"></i>
                            <input type="search" v-model="stock.recherche" placeholder="Article, magasin...">
                        </div>
                    </div>
                </div>
                <div class="gn-tabs2">
                    <button type="button" :class="{ 'gn-tabs2--on': stock.onglet === 'rup' }" @click="stock.onglet = 'rup'">Ruptures <b>{{ formatNombre(stockFiltre('rup').length) }}</b></button>
                    <button type="button" :class="{ 'gn-tabs2--on': stock.onglet === 'min' }" @click="stock.onglet = 'min'">Sous le minimum <b>{{ formatNombre(stockFiltre('min').length) }}</b></button>
                </div>
                <div class="gn-list">
                    <template v-if="stock.charge">
                        <div v-if="!listeStock.length" class="gn-empty"><i class="ri-checkbox-circle-line gn-ok"></i>Rien à signaler.</div>
                        <div v-for="(s, i) in listeStock.slice(0, stock.limite)" :key="i" class="gn-stk">
                            <div class="gn-min0">
                                <b>{{ s.libelle }}</b><br>
                                <small>{{ s.nommaga }} · {{ s.stockmin > 0 ? 'minimum ' + formatNombre(s.stockmin) : 'pas de minimum défini' }}</small>
                            </div>
                            <div class="gn-lvl">
                                <span v-if="s.stock < 0" class="gn-badge gn-badge--bad">négatif</span>
                                <span class="gn-lvl__bar"><i :style="{ width: Math.max(4, s.stockmin > 0 ? Math.min(100, s.stock / s.stockmin * 100) : 0) + '%', background: s.stock <= 0 ? '#dc2626' : '#d97706' }"></i></span>
                                <span class="gn-qty" :style="{ color: s.stock <= 0 ? '#dc2626' : '#d97706' }">{{ formatNombre(s.stock) }}</span>
                            </div>
                        </div>
                        <div v-if="listeStock.length > stock.limite" class="gn-more">
                            <button type="button" class="gn-link" @click="stock.limite += 100">Afficher 100 de plus ({{ listeStock.length - stock.limite }} restants)</button>
                        </div>
                    </template>
                    <template v-else><div class="gn-card__body"><div v-for="n in 6" :key="n" class="gn-skel"></div></div></template>
                </div>
            </div>

            <div class="gn-card">
                <div class="gn-card__head">
                    <h2><i class="ri-funds-line"></i> Bénéfice des 14 derniers jours</h2>
                    <button type="button" class="gn-link" @click="ouvrirOngletMarge">Analyse complète <i class="ri-arrow-right-line"></i></button>
                </div>
                <div class="gn-card__body">
                    <template v-if="benef.charge">
                        <apexcharts v-if="benef.jours.length" type="area" height="180" :options="optionsBenef" :series="seriesBenef"></apexcharts>
                        <div v-else class="gn-empty"><i class="ri-funds-line"></i>Aucune vente sur les 14 derniers jours.</div>
                        <div class="gn-benef-foot">
                            <span>Total 14 jours <b>{{ formatMoney(benef.total) }} F</b></span>
                            <span>Marge réelle <b class="gn-blue">{{ formatPourcent(benef.ventes ? benef.total / benef.ventes * 100 : 0) }} %</b></span>
                        </div>
                    </template>
                    <div v-else class="gn-skel gn-skel--chart" style="height:180px"></div>
                </div>
            </div>
        </div>

        <!-- 7. Mouvements des articles (chargés sur demande) -->
        <div class="gn-card">
            <div class="gn-card__head">
                <h2><i class="ri-arrow-left-right-line"></i> Mouvements des articles <span>· entrées et sorties de stock</span></h2>
            </div>

            <div v-if="!mvt.charge" class="gn-lazy">
                <i class="ri-database-2-line"></i>
                <div>
                    <b>Historique non chargé</b><br>
                    L'historique complet des mouvements est volumineux : il ne se charge que sur demande.
                </div>
                <button type="button" class="gn-btn-p" :disabled="mvt.chargement" @click="chargerMouvements">
                    <i :class="mvt.chargement ? 'ri-loader-4-line gn-spin' : 'ri-download-2-line'"></i>
                    {{ mvt.chargement ? 'Chargement...' : 'Afficher les mouvements' }}
                </button>
            </div>

            <template v-else>
                <div class="gn-mvt-tools">
                    <div class="gn-msearch gn-msearch--grow">
                        <i class="ri-search-line"></i>
                        <input type="search" v-model="mvt.recherche" placeholder="Article, n° de document, tiers...">
                    </div>
                    <select class="gn-input" v-model="mvt.magasin" aria-label="Magasin">
                        <option value="">Tous les magasins</option>
                        <option v-for="m in mvt.magasins" :key="m" :value="m">{{ m }}</option>
                    </select>
                    <input type="date" class="gn-input" v-model="mvt.dateDebut" aria-label="Du">
                    <input type="date" class="gn-input" v-model="mvt.dateFin" aria-label="Au">
                    <div class="gn-seg" role="group">
                        <button type="button" :class="{ 'gn-seg--on': mvt.sens === '' }" @click="mvt.sens = ''">Tout</button>
                        <button type="button" :class="{ 'gn-seg--on': mvt.sens === '+' }" @click="mvt.sens = '+'">Entrées</button>
                        <button type="button" :class="{ 'gn-seg--on': mvt.sens === '-' }" @click="mvt.sens = '-'">Sorties</button>
                    </div>
                </div>
                <div class="gn-tiers gn-tiers--lg">
                    <table>
                        <thead>
                        <tr>
                            <th class="th">Date</th>
                            <th class="th">Document</th>
                            <th class="th">Article</th>
                            <th class="th gn-hide-sm">Magasin</th>
                            <th class="th gn-hide-sm">Tiers</th>
                            <th class="th gn-hide-sm">Saisi par</th>
                            <th class="th th--right">Avant</th>
                            <th class="th th--right">Entrée</th>
                            <th class="th th--right">Sortie</th>
                            <th class="th th--right">Après</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-if="!mouvementsFiltres.length">
                            <td colspan="10"><div class="gn-empty"><i class="ri-search-eye-line"></i>Aucun mouvement ne correspond.</div></td>
                        </tr>
                        <tr v-for="(x, i) in mouvementsPage" :key="i" class="gn-row">
                            <td class="td">{{ formatDate(x.docdate) }}</td>
                            <td class="td"><span class="gn-dtype">{{ x.typdoc || '-' }}</span> {{ x.docref }}</td>
                            <td class="td"><b>{{ x.libelle }}</b></td>
                            <td class="td gn-hide-sm">{{ x.nommaga }}</td>
                            <td class="td gn-hide-sm">{{ x.societefrsclt || '-' }}</td>
                            <td class="td gn-hide-sm">{{ x.saisiepar || '-' }}</td>
                            <td class="td td--right">{{ formatNombre(x.stck_init) }}</td>
                            <td class="td td--right gn-in">{{ x.qte_entre ? '+' + formatNombre(x.qte_entre) : '' }}</td>
                            <td class="td td--right gn-out">{{ x.qte_sorti ? '-' + formatNombre(x.qte_sorti) : '' }}</td>
                            <td class="td td--right"><b>{{ formatNombre(x.sctk_final) }}</b></td>
                        </tr>
                        </tbody>
                    </table>
                </div>
                <div class="gn-pager">
                    <span>{{ formatNombre(mouvementsFiltres.length) }} mouvement{{ mouvementsFiltres.length > 1 ? 's' : '' }}</span>
                    <span class="gn-pager__pg">
                        <button type="button" :disabled="mvt.page <= 1" @click="mvt.page--">‹</button>
                        {{ mvt.page }} / {{ nbPagesMvt }}
                        <button type="button" :disabled="mvt.page >= nbPagesMvt" @click="mvt.page++">›</button>
                    </span>
                </div>
            </template>
        </div>

    </div>

</template>

<script>
    import {currencyFormatDE, currentYear, randomString} from "@/allfunction";
    import * as JwtService from "@/common/jwt.service";
    const axios =require('axios');
    import VueApexCharts from "vue3-apexcharts";
    import { lireCacheTB, ecrireCacheTB } from "@/service/cacheTableauDeBord";
    import bandeaucachetb from "@/components/views/pages/tableaudebord/bandeaucachetb";

    const MOIS = ['janv.','févr.','mars','avr.','mai','juin','juil.','août','sept.','oct.','nov.','déc.'];
    const MOIS_LONGS = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
    const COULEURS = ['#7c5ae0','#2563eb','#059669','#d97706','#db2777','#0891b2','#65a30d','#9333ea'];

    /* Date locale AAAA-MM-JJ (pas toISOString, qui passe en UTC). */
    const isoLocal = (d) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };

    export default {
        /* eslint-disable */
        name: "general",
        emits: ['update:updatedatamg'],
        components: {
            bandeaucachetb,
            apexcharts: VueApexCharts,
        },
        data() {
            const now = new Date();
            return {
                depuisCache: false,
                heureCache: '',
                nbChargements: 0,
                derniereMaj: '',
                /* cache 5 min (cacheTableauDeBord) : forcer = Actualiser ; tsTB = heure de la donnée la plus ancienne affichée */
                forcerTB: false,
                tsTB: null,
                anneeCourante: currentYear(),
                moisCourant: now.getMonth(),

                /* CA mensuel par année : { 2026: [12 montants], ... } */
                ca: { charge: false, parAnnee: {} },
                caMag: { charge: false, series: [] },
                vueCa: 'total',
                nbAnnees: 2,

                /* top articles : { magasin: [{ nom, ca }] } */
                top: { charge: false, parMagasin: {}, magasins: [], magasin: '' },

                periodesTiers: [
                    { code: '1M', label: '1 mois' },
                    { code: '6M', label: '6 mois' },
                    { code: '1Y', label: '1 an' },
                    { code: '3Y', label: '3 ans' },
                ],
                configTiers: [
                    { cle: 'clt', titre: 'Créances clients', icone: 'ri-hand-coin-line', colNom: 'Client', colMontant: 'CA facturé', colReste: 'Reste à encaisser', couleur: '#2563eb', vide: 'Aucune facture client sur la période.' },
                    { cle: 'frs', titre: 'Dettes fournisseurs', icone: 'ri-bank-card-line', colNom: 'Fournisseur', colMontant: 'Achats', colReste: 'Reste à payer', couleur: '#dc2626', vide: 'Aucune facture fournisseur sur la période.' },
                ],
                tiers: {
                    clt: { charge: false, periode: '1Y', recherche: '', tri: 'reste', lignes: [] },
                    frs: { charge: false, periode: '1Y', recherche: '', tri: 'reste', lignes: [] },
                },

                stock: { charge: false, ruptures: [], sousmin: [], onglet: 'rup', recherche: '', magasin: '', limite: 100 },

                benef: { charge: false, jours: [], total: 0, ventes: 0 },

                mvt: { charge: false, chargement: false, lignes: [], magasins: [], magasin: '', recherche: '', dateDebut: '', dateFin: '', sens: '', page: 1, parPage: 25 },
            };
        },
        computed: {
            dateDuJour() {
                const s = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
                return s.charAt(0).toUpperCase() + s.slice(1);
            },

            /* ---------- KPI CA ---------- */
            kpiCa() {
                const a = this.anneeCourante, m = this.moisCourant;
                const cur = this.ca.parAnnee[a] || [], prev = this.ca.parAnnee[a - 1] || null;
                const mois = num(cur[m]);
                const moisPrec = m > 0 ? num(cur[m - 1]) : (prev ? num(prev[11]) : 0);
                const annee = cur.slice(0, m + 1).reduce((s, v) => s + num(v), 0);
                const anneePrec = prev ? prev.slice(0, m + 1).reduce((s, v) => s + num(v), 0) : 0;
                return { mois, moisPrec, annee, anneePrec };
            },
            sparkline() {
                const cur = (this.ca.parAnnee[this.anneeCourante] || []).slice(0, this.moisCourant + 1).map(num);
                if (cur.length < 2) return '';
                const mx = Math.max(...cur) || 1, w = 110, h = 36;
                return cur.map((v, i) => (i * w / (cur.length - 1)).toFixed(1) + ',' + (h - 2 - v / mx * (h - 4)).toFixed(1)).join(' ');
            },

            /* ---------- graphique CA ---------- */
            anneesAffichees() {
                const a = this.anneeCourante;
                return [a - 2, a - 1, a].slice(3 - this.nbAnnees).filter(y => this.ca.parAnnee[y]);
            },
            seriesCa() {
                return this.anneesAffichees.map(y => ({
                    name: String(y),
                    type: y === this.anneeCourante ? 'column' : 'line',
                    data: this.ca.parAnnee[y].map(v => Math.round(num(v))),
                }));
            },
            optionsCa() {
                const ys = this.anneesAffichees, a = this.anneeCourante;
                return Object.assign(this.optionsBase(), {
                    colors: ys.map(y => y === a ? '#7c5ae0' : y === a - 1 ? '#94a3b8' : '#cbd5e1'),
                    stroke: { width: ys.map(y => y === a ? 0 : 2), dashArray: ys.map(y => y === a - 2 ? 5 : 0) },
                    markers: { size: ys.map(y => y === a ? 0 : 3) },
                    plotOptions: { bar: { columnWidth: '55%', borderRadius: 3 } },
                    xaxis: { categories: MOIS, labels: { style: { colors: '#94a3b8' } } },
                });
            },
            optionsCaMag() {
                return Object.assign(this.optionsBase(), {
                    chart: { type: 'bar', stacked: true, toolbar: { show: false }, fontFamily: 'inherit', animations: { enabled: false } },
                    colors: COULEURS,
                    plotOptions: { bar: { columnWidth: '55%', borderRadius: 3 } },
                    xaxis: { categories: MOIS, labels: { style: { colors: '#94a3b8' } } },
                });
            },

            /* ---------- top articles ---------- */
            topArticles() {
                const src = this.top.magasin ? { [this.top.magasin]: this.top.parMagasin[this.top.magasin] || [] } : this.top.parMagasin;
                const m = {};
                for (const liste of Object.values(src)) for (const t of liste) m[t.nom] = (m[t.nom] || 0) + t.ca;
                return Object.entries(m).map(([nom, ca]) => ({ nom, ca })).sort((x, y) => y.ca - x.ca).slice(0, 8);
            },

            /* ---------- créances / dettes ---------- */
            lignesTiers() {
                const r = {};
                for (const k of ['clt', 'frs']) {
                    const t = this.tiers[k], q = t.recherche.trim().toLowerCase();
                    const l = t.lignes.filter(x => !q || x.nom.toLowerCase().includes(q));
                    l.sort((a, b) => t.tri === 'nom' ? a.nom.localeCompare(b.nom) : b[t.tri] - a[t.tri]);
                    r[k] = l;
                }
                return r;
            },
            totauxTiers() {
                const r = {};
                for (const k of ['clt', 'frs']) {
                    const all = this.tiers[k].lignes, f = this.lignesTiers[k];
                    r[k] = {
                        montant: all.reduce((s, x) => s + x.montant, 0),
                        reste: all.reduce((s, x) => s + Math.max(0, x.reste), 0),
                        nb: all.filter(x => x.reste > 0).length,
                        montantFiltre: f.reduce((s, x) => s + x.montant, 0),
                        resteFiltre: f.reduce((s, x) => s + Math.max(0, x.reste), 0),
                    };
                }
                return r;
            },

            /* ---------- stock ---------- */
            magasinsStock() {
                return [...new Set(this.stock.ruptures.concat(this.stock.sousmin).map(s => s.nommaga))].sort();
            },
            listeStock() {
                return this.stockFiltre(this.stock.onglet);
            },

            /* ---------- alertes ---------- */
            alertes() {
                const a = [];
                if (this.stock.charge && this.stock.ruptures.length) a.push({ niveau: 'bad', icone: 'ri-alarm-warning-line', cible: 'stock', onglet: 'rup', texte: '<b>' + this.formatNombre(this.stock.ruptures.length) + ' article' + (this.stock.ruptures.length > 1 ? 's' : '') + ' en rupture</b> de stock' });
                if (this.stock.charge && this.stock.sousmin.length) a.push({ niveau: 'warn', icone: 'ri-arrow-down-circle-line', cible: 'stock', onglet: 'min', texte: '<b>' + this.formatNombre(this.stock.sousmin.length) + ' article' + (this.stock.sousmin.length > 1 ? 's' : '') + '</b> sous le stock minimum' });
                if (this.tiers.clt.charge && this.totauxTiers.clt.reste > 0) a.push({ niveau: 'info', icone: 'ri-hand-coin-line', cible: 'tiers', texte: '<b>' + this.formatMoney(this.totauxTiers.clt.reste) + ' F</b> à encaisser chez ' + this.totauxTiers.clt.nb + ' client' + (this.totauxTiers.clt.nb > 1 ? 's' : '') });
                return a;
            },

            /* ---------- bénéfice ---------- */
            seriesBenef() {
                return [{ name: 'Bénéfice', data: this.benef.jours.map(j => Math.round(j.bn)) }];
            },
            optionsBenef() {
                return Object.assign(this.optionsBase(), {
                    chart: { type: 'area', toolbar: { show: false }, fontFamily: 'inherit', animations: { enabled: false } },
                    colors: ['#059669'],
                    stroke: { curve: 'smooth', width: 2 },
                    fill: { type: 'gradient', gradient: { opacityFrom: 0.35, opacityTo: 0.03 } },
                    xaxis: { categories: this.benef.jours.map(j => this.formatDate(j.j).slice(0, 5)), labels: { show: false }, axisTicks: { show: false } },
                    legend: { show: false },
                });
            },

            /* ---------- mouvements ---------- */
            mouvementsFiltres() {
                const m = this.mvt, q = m.recherche.trim().toLowerCase();
                return m.lignes.filter(x =>
                    (!m.magasin || x.nommaga === m.magasin) &&
                    (!m.sens || x.operationsigne === m.sens) &&
                    (!m.dateDebut || (x.docdate && x.docdate >= m.dateDebut)) &&
                    (!m.dateFin || (x.docdate && x.docdate <= m.dateFin)) &&
                    (!q || ((x.libelle || '') + ' ' + (x.docref || '') + ' ' + (x.societefrsclt || '')).toLowerCase().includes(q))
                );
            },
            nbPagesMvt() {
                return Math.max(1, Math.ceil(this.mouvementsFiltres.length / this.mvt.parPage));
            },
            mouvementsPage() {
                const p = Math.min(this.mvt.page, this.nbPagesMvt);
                return this.mouvementsFiltres.slice((p - 1) * this.mvt.parPage, p * this.mvt.parPage);
            },
        },
        watch: {
            'mvt.recherche'() { this.mvt.page = 1; },
            'mvt.magasin'() { this.mvt.page = 1; },
            'mvt.dateDebut'() { this.mvt.page = 1; },
            'mvt.dateFin'() { this.mvt.page = 1; },
            'mvt.sens'() { this.mvt.page = 1; },
            'stock.onglet'() { this.stock.limite = 100; },
            'stock.recherche'() { this.stock.limite = 100; },
        },
        methods: {

            /* ---------- appels API ---------- */
            entetes() {
                return {
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + JwtService.getToken(),
                        'csrftoken': this.$store.state.appsociete
                    },
                    withCredentials: true // Permet l'envoi de cookies et autres informations d'authentification
                };
            },
            url(chemin) {
                return process.env.VUE_APP_API_URL + '/egl745874tb771714dfe/' + chemin + '/' + this.$store.state.appsociete + '/' + randomString();
            },
            async appel(fn) {
                this.nbChargements++;
                try {
                    await fn();
                } catch (e) {
                    toastr['error']("Une erreur est subvenue. <br/> veuillez actualiser la page.", "<b></b>");
                } finally {
                    this.nbChargements--;
                    if (this.nbChargements === 0) {
                        const h = this.tsTB ? new Date(this.tsTB) : new Date();
                        this.depuisCache = this.tsTB !== null;
                        this.tsTB = null;
                        this.forcerTB = false;
                        this.derniereMaj = String(h.getHours()).padStart(2, '0') + ':' + String(h.getMinutes()).padStart(2, '0');
                        this.heureCache = this.derniereMaj;
                    }
                }
            },

            /* Réponse serveur (r.data) servie depuis le cache si elle a moins de 5 min ; seules les réponses success sont gardées. */
            async lire(cle, params, requete) {
                const societe = this.$store.state.appsociete;
                if (!this.forcerTB) {
                    const ligne = await lireCacheTB(societe, 'general_' + cle, params);
                    if (ligne) {
                        if (this.tsTB === null || ligne.t < this.tsTB) this.tsTB = ligne.t;
                        return { data: ligne.data };
                    }
                }
                const r = await requete();
                if (r.data && r.data.success) await ecrireCacheTB(societe, 'general_' + cle, params, r.data);
                return r;
            },

            chargerCa() {
                return this.appel(async () => {
                    const r = await this.lire('ca', {}, () => axios.get(this.url('dash/graph/suivica/3Y'), this.entetes()));
                    const p = {};
                    if (r.data.success && r.data.allgraphs && r.data.allgraphs.series) {
                        for (const s of r.data.allgraphs.series) p[parseInt(s.name, 10)] = (s.data || []).map(num);
                    }
                    this.ca.parAnnee = p;
                    this.ca.charge = true;
                });
            },

            chargerCaMagasins() {
                return this.appel(async () => {
                    const r = await this.lire('camag', { annee: this.anneeCourante }, () => axios.get(this.url('dash/graph/pointdevente/' + this.anneeCourante), this.entetes()));
                    const s = r.data.success && r.data.allgraphs && r.data.allgraphs.series ? r.data.allgraphs.series : [];
                    this.caMag.series = s.map(x => ({ name: x.name, data: (x.data || []).map(v => Math.round(num(v))) }));
                    this.caMag.charge = true;
                });
            },

            chargerTop() {
                return this.appel(async () => {
                    const r = await this.lire('top', { annee: this.anneeCourante }, () => axios.get(this.url('dash/graph/top6desarticlesvendu/' + this.anneeCourante), this.entetes()));
                    const p = {};
                    if (r.data.success && Array.isArray(r.data.allgraphs)) {
                        for (const g of r.data.allgraphs) {
                            const mag = (g.chartOptions && g.chartOptions.title && g.chartOptions.title.text) || '(magasin)';
                            const labels = (g.chartOptions && g.chartOptions.labels) || [];
                            /* libellé serveur : « nom ( 1 390 905 FCFA ) » ; on garde le nom */
                            p[mag] = labels.map((l, i) => ({ nom: String(l).replace(/\s*\([^()]*\)\s*$/, '').trim(), ca: num((g.series || [])[i]) }));
                        }
                    }
                    this.top.parMagasin = p;
                    this.top.magasins = Object.keys(p).sort();
                    this.top.charge = true;
                });
            },

            chargerTiers(cle) {
                const t = this.tiers[cle];
                t.charge = false;
                return this.appel(async () => {
                    const route = cle === 'clt' ? 'dash/graph/pointcreance784517788/' : 'dash/graph/pointdette784517788/';
                    const r = await this.lire('tiers', { cle, periode: t.periode }, () => axios.get(this.url(route + t.periode), this.entetes()));
                    const dm = r.data.success && Array.isArray(r.data.dm) ? r.data.dm : [];
                    t.lignes = dm.map(x => {
                        const montant = num(cle === 'clt' ? x.ca : x.charges);
                        const reste = num(cle === 'clt' ? x.creance : x.dette);
                        return { nom: x.namecltfrs || '(sans nom)', montant, reste, paye: montant ? (1 - reste / montant) * 100 : 100 };
                    });
                    t.charge = true;
                });
            },

            chargerStock() {
                return this.appel(async () => {
                    const r = await this.lire('stockalertes', {}, () => axios.post(this.url('dashboard/stockalertes'), {}, this.entetes()));
                    const m = r.data.success ? r.data.m : {};
                    this.stock.ruptures = m.ruptures || [];
                    this.stock.sousmin = m.sousmin || [];
                    this.stock.charge = true;
                });
            },

            chargerBenefice() {
                return this.appel(async () => {
                    const fin = new Date(), debut = new Date();
                    debut.setDate(debut.getDate() - 13);
                    const q = new URLSearchParams({ typeperiode: 'custom', datedebut: isoLocal(debut), datefin: isoLocal(fin), _: Date.now().toString() });
                    const r = await this.lire('benef', { debut: isoLocal(debut), fin: isoLocal(fin) }, () => axios.get(this.url('dash/graph/587999fff9ddbenef784517788/custom') + '?' + q.toString(), this.entetes()));
                    const parJour = {};
                    for (let d = new Date(debut); d <= fin; d.setDate(d.getDate() + 1)) parJour[isoLocal(d)] = { j: isoLocal(d), bn: 0 };
                    let total = 0, ventes = 0;
                    for (const x of (r.data.success && r.data.dm) || []) {
                        const bn = num(x.benef);
                        const signe = x.document === 'Facture (Vente)' ? 1 : -1;
                        const j = String(x.datecreated || '').slice(0, 10);
                        if (parJour[j]) parJour[j].bn += bn;
                        total += bn;
                        ventes += num(x.pu) * num(x.qtitetotal) * signe - num(x.remisetotal);
                    }
                    this.benef.jours = Object.values(parJour);
                    this.benef.total = total;
                    this.benef.ventes = ventes;
                    this.benef.charge = true;
                });
            },

            chargerMouvements() {
                this.mvt.chargement = true;
                return this.appel(async () => {
                    const r = await this.lire('mouvements', {}, () => axios.get(this.url('dash/graph/45745458656ddffallstock784517788'), this.entetes()));
                    if (r.data.success) {
                        this.mvt.lignes = (r.data.suivistck || []).slice().sort((a, b) => String(b.docdate || '').localeCompare(String(a.docdate || '')));
                        this.mvt.magasins = (r.data.mg || []).slice().sort();
                        this.$emit("update:updatedatamg", r.data.mg || []);
                    } else {
                        this.mvt.lignes = [];
                    }
                    const fin = new Date(), debut = new Date(fin.getFullYear(), fin.getMonth(), 1);
                    if (!this.mvt.dateDebut) this.mvt.dateDebut = isoLocal(debut);
                    if (!this.mvt.dateFin) this.mvt.dateFin = isoLocal(fin);
                    this.mvt.charge = true;
                }).finally(() => { this.mvt.chargement = false; });
            },

            toutCharger(force) {
                this.forcerTB = force === true;
                this.chargerCa();
                this.chargerCaMagasins();
                this.chargerTop();
                this.chargerTiers('clt');
                this.chargerTiers('frs');
                this.chargerStock();
                this.chargerBenefice();
                if (this.mvt.charge) this.chargerMouvements();
            },

            /* ---------- interactions ---------- */
            stockFiltre(onglet) {
                const src = onglet === 'rup' ? this.stock.ruptures : this.stock.sousmin;
                const q = this.stock.recherche.trim().toLowerCase(), mag = this.stock.magasin;
                return src.filter(s => (!mag || s.nommaga === mag) && (!q || ((s.libelle || '') + ' ' + (s.nommaga || '')).toLowerCase().includes(q)));
            },
            aller(cible, onglet) {
                if (onglet) this.stock.onglet = onglet;
                const el = this.$refs[cible];
                if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            },
            ouvrirOngletMarge() {
                const a = document.getElementById('margebeneficiaire-item');
                if (a) a.click();
            },
            libellePeriodeTiers(code) {
                const p = this.periodesTiers.find(x => x.code === code);
                return p ? p.label : code;
            },

            /* ---------- graphiques ---------- */
            optionsBase() {
                return {
                    chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: false }, fontFamily: 'inherit', animations: { enabled: false } },
                    dataLabels: { enabled: false },
                    grid: { borderColor: '#e2e8f0', strokeDashArray: 3 },
                    legend: { position: 'top', horizontalAlign: 'right' },
                    yaxis: { labels: { formatter: v => this.abrege(v), style: { colors: '#94a3b8' } } },
                    tooltip: { shared: true, intersect: false, y: { formatter: v => this.formatMoney(v) + ' F' } },
                };
            },

            /* ---------- formats ---------- */
            formatMoney(v) {
                return currencyFormatDE(Math.round(num(v)));
            },
            /* Quantités et compteurs : séparateur de milliers, sans devise. */
            formatNombre(v) {
                return num(v).toLocaleString('fr-FR', { maximumFractionDigits: 2 }).replace(/[  ]/g, ' ');
            },
            formatPourcent(v) {
                return num(v).toFixed(1).replace('.', ',');
            },
            pourcent(part, total) {
                return total ? Math.round(part / total * 100) : 0;
            },
            fleche(actuel, prec) {
                if (!prec) return '';
                const v = (actuel - prec) / Math.abs(prec) * 100;
                return (v >= 0 ? '▲ ' : '▼ ') + Math.abs(Math.round(v)) + ' %';
            },
            abrege(v) {
                const a = Math.abs(v);
                if (a >= 1e6) return (v / 1e6).toFixed(1).replace('.0', '') + ' M';
                if (a >= 1e3) return Math.round(v / 1e3) + ' k';
                return Math.round(v);
            },
            nomMois(i) {
                return MOIS_LONGS[i];
            },
            formatDate(j) {
                if (!j) return '';
                const s = String(j).slice(0, 10).split('-');
                return s.length === 3 ? s[2] + '/' + s[1] + '/' + s[0] : j;
            },
            couleur(nom) {
                let h = 0;
                for (let i = 0; i < nom.length; i++) h = (h * 31 + nom.charCodeAt(i)) >>> 0;
                return COULEURS[h % COULEURS.length];
            },
            initiales(nom) {
                return nom.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
            },
        },
        mounted() {
            this.toutCharger();
        }
    }
</script>

<style scoped>

.gn-page { padding-bottom: 1rem; }
.gn-min0 { min-width: 0; }

/* boutons, segments, champs */
.gn-btn { height: 2.1rem; padding: 0 0.75rem; display: inline-flex; gap: 0.35rem; align-items: center; background: #fff; border: 1px solid var(--cf-border); border-radius: var(--cf-radius-sm); color: var(--cf-muted); cursor: pointer; font-size: 0.8rem; font-weight: 500; }
.gn-btn:hover:not(:disabled) { color: rgb(var(--primary)); }
.gn-btn:disabled { opacity: 0.6; cursor: default; }
.gn-spin { display: inline-block; animation: gn-rot 0.9s linear infinite; }
@keyframes gn-rot { to { transform: rotate(360deg); } }
.gn-tools { display: flex; flex-wrap: wrap; gap: 0.4rem; align-items: center; }
.gn-seg { display: inline-flex; flex-wrap: wrap; background: #fff; border: 1px solid var(--cf-border); border-radius: var(--cf-radius-sm); padding: 3px; gap: 2px; }
.gn-seg button { border: 0; background: transparent; font-size: 0.76rem; font-weight: 500; padding: 0.26rem 0.6rem; border-radius: var(--cf-radius-xs); color: var(--cf-muted); cursor: pointer; white-space: nowrap; }
.gn-seg button:hover { color: rgb(var(--primary)); }
.gn-seg button.gn-seg--on { background: rgb(var(--primary)); color: #fff; }
.gn-input { background: #fff; border: 1px solid var(--cf-border); border-radius: var(--cf-radius-sm); padding: 0.33rem 0.55rem; font-size: 0.78rem; color: var(--cf-ink); max-width: 100%; }
.gn-msearch { position: relative; }
.gn-msearch i { position: absolute; left: 0.55rem; top: 50%; transform: translateY(-50%); color: var(--cf-faint); }
.gn-msearch input { padding: 0.35rem 0.6rem 0.35rem 1.8rem; border: 1px solid var(--cf-border); border-radius: var(--cf-radius-sm); font-size: 0.78rem; width: 170px; background: var(--cf-surface); }
.gn-msearch input:focus { outline: none; border-color: rgb(var(--primary)); background: #fff; }
.gn-msearch--grow { flex: 1 1 240px; }
.gn-msearch--grow input { width: 100%; }
.gn-link { border: 0; background: none; padding: 0; color: rgb(var(--primary)); font-weight: 600; font-size: 0.78rem; cursor: pointer; display: inline-flex; gap: 0.25rem; align-items: center; }
.gn-btn-p { border: 0; background: rgb(var(--primary)); color: #fff; font-weight: 600; font-size: 0.82rem; padding: 0.55rem 1rem; border-radius: var(--cf-radius-sm); cursor: pointer; display: inline-flex; gap: 0.4rem; align-items: center; }
.gn-btn-p:disabled { opacity: 0.7; cursor: default; }

/* alertes */
.gn-alerts { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 1rem; }
.gn-alert { display: flex; gap: 0.5rem; align-items: center; padding: 0.5rem 0.8rem; border-radius: var(--cf-radius-sm); font-size: 0.8rem; cursor: pointer; text-align: left; }
.gn-alert i { font-size: 1rem; }
.gn-alert--bad { background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; }
.gn-alert--warn { background: #fffbeb; color: #92400e; border: 1px solid #fde68a; }
.gn-alert--info { background: #eff6ff; color: #1e40af; border: 1px solid #bfdbfe; }

/* KPI */
.gn-kpis { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.75rem; margin-bottom: 1rem; }
@media (min-width: 1100px) { .gn-kpis { grid-template-columns: 1.25fr 1fr 1fr 1fr 1fr; } }
.gn-kpi { background: #fff; border: 1px solid var(--cf-border); border-radius: var(--cf-radius); box-shadow: var(--cf-shadow-sm); padding: 0.9rem 1rem; min-width: 0; position: relative; overflow: hidden; transition: box-shadow 0.15s, transform 0.15s; }
.gn-kpi[role="button"] { cursor: pointer; }
.gn-kpi:hover { box-shadow: var(--cf-shadow-md); transform: translateY(-1px); }
.gn-kpi__lbl { display: flex; justify-content: space-between; align-items: center; color: var(--cf-muted); font-size: 0.78rem; font-weight: 500; }
.gn-kpi__lbl i { width: 1.9rem; height: 1.9rem; display: inline-flex; align-items: center; justify-content: center; border-radius: 8px; font-size: 1rem; }
.gn-kpi__val { font-size: 1.4rem; font-weight: 800; color: var(--cf-ink-strong); margin: 0.35rem 0 0.1rem; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
.gn-kpi__val small { font-size: 0.75rem; font-weight: 500; color: var(--cf-muted); }
.gn-kpi__sub { font-size: 0.72rem; color: var(--cf-muted); position: relative; z-index: 1; }
.gn-kpi--main { background: linear-gradient(135deg, rgb(var(--primary)) 0%, #6d4bd0 100%); border: 0; color: #fff; }
.gn-kpi--main .gn-kpi__lbl, .gn-kpi--main .gn-kpi__sub { color: rgb(255 255 255 / 0.85); }
.gn-kpi--main .gn-kpi__lbl i { background: rgb(255 255 255 / 0.18); }
.gn-kpi--main .gn-kpi__val, .gn-kpi--main .gn-kpi__val small { color: #fff; }
.gn-spark { position: absolute; right: 0; bottom: 0; opacity: 0.35; }
.gn-up { color: #059669; font-weight: 600; }
.gn-down { color: #dc2626; font-weight: 600; }
.gn-up--light { color: #bbf7d0; font-weight: 700; }
.gn-down--light { color: #fecaca; font-weight: 700; }
.gn-i-vio { background: #f1ecfd; color: #7c5ae0; }
.gn-i-blue { background: #eff6ff; color: #2563eb; }
.gn-i-red { background: #fef2f2; color: #dc2626; }
.gn-i-amb { background: #fff7ed; color: #d97706; }
.gn-blue { color: #2563eb; }
.gn-ok { color: #059669 !important; }
.gn-strong { font-weight: 700; }

/* grilles et cartes */
.gn-grid2 { display: grid; gap: 1rem; margin-bottom: 1rem; scroll-margin-top: 80px; }
@media (min-width: 1024px) {
    .gn-grid2 { grid-template-columns: minmax(0, 1.7fr) minmax(0, 1fr); }
    .gn-grid2--eq { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
}
.gn-card { background: #fff; border: 1px solid var(--cf-border); border-radius: var(--cf-radius); box-shadow: var(--cf-shadow-sm); min-width: 0; display: flex; flex-direction: column; }
.gn-card__head { display: flex; justify-content: space-between; align-items: center; gap: 0.5rem; flex-wrap: wrap; padding: 0.75rem 1rem; border-bottom: 1px solid var(--cf-border); }
.gn-card__head h2 { margin: 0; font-size: 0.9rem; font-weight: 700; color: var(--cf-ink-strong); display: flex; align-items: center; gap: 0.4rem; }
.gn-card__head h2 i { color: rgb(var(--primary)); }
.gn-card__head h2 span { font-size: 0.75rem; font-weight: 400; color: var(--cf-muted); }
.gn-card__body { padding: 0.85rem 1rem; }

/* classement */
.gn-rank { display: grid; gap: 0.55rem; }
.gn-rank__row { display: grid; grid-template-columns: 1.6rem minmax(0, 1fr) auto; gap: 0.6rem; align-items: center; }
.gn-rank__n { width: 1.6rem; height: 1.6rem; border-radius: 6px; background: var(--cf-surface-alt); display: inline-flex; align-items: center; justify-content: center; font-size: 0.72rem; font-weight: 700; color: var(--cf-muted); }
.gn-rank__row:nth-child(1) .gn-rank__n { background: #fef3c7; color: #b45309; }
.gn-rank__row:nth-child(2) .gn-rank__n { background: #e2e8f0; color: #475569; }
.gn-rank__row:nth-child(3) .gn-rank__n { background: #ffedd5; color: #c2410c; }
.gn-rank__name { font-size: 0.82rem; font-weight: 600; color: var(--cf-ink-strong); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.gn-rank__bar { height: 6px; background: var(--cf-surface-alt); border-radius: 99px; overflow: hidden; margin-top: 0.25rem; }
.gn-rank__bar i { display: block; height: 100%; border-radius: 99px; background: linear-gradient(90deg, #7c5ae0, #a78bfa); }
.gn-rank__val { text-align: right; font-size: 0.8rem; font-weight: 700; font-variant-numeric: tabular-nums; white-space: nowrap; }
.gn-rank__val small { display: block; font-size: 0.68rem; font-weight: 500; color: var(--cf-muted); }

/* tableaux */
.gn-tiers { overflow: auto; max-height: 340px; }
.gn-tiers--lg { max-height: 520px; }
.gn-tiers table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 0.8rem; }
.th { position: sticky; top: 0; z-index: 1; padding: 0.55rem 0.8rem; font-weight: 600; font-size: 0.7rem; color: var(--cf-ink); background: var(--cf-surface-alt); border-bottom: 1px solid var(--cf-border); text-align: left; white-space: nowrap; }
.th--right { text-align: right; }
.gn-th--click { cursor: pointer; user-select: none; }
.gn-th--click:hover { color: rgb(var(--primary)); }
.td { padding: 0.55rem 0.8rem; border-bottom: 1px solid var(--cf-surface-alt); font-variant-numeric: tabular-nums; white-space: nowrap; color: var(--cf-ink); }
.td--right { text-align: right; }
.gn-row:hover .td { background: var(--cf-surface); }
.gn-tiers tfoot .td { position: sticky; bottom: 0; background: #1e293b; color: #fff; font-weight: 700; border: 0; }
.gn-who { display: flex; gap: 0.55rem; align-items: center; }
.gn-who b { font-weight: 600; color: var(--cf-ink-strong); }
.gn-av { width: 1.8rem; height: 1.8rem; border-radius: 99px; display: inline-flex; align-items: center; justify-content: center; color: #fff; font-size: 0.68rem; font-weight: 700; flex: 0 0 auto; }
.gn-pct { display: inline-flex; align-items: center; gap: 0.4rem; }
.gn-pct span { width: 52px; height: 6px; border-radius: 99px; background: var(--cf-surface-alt); overflow: hidden; display: inline-block; }
.gn-pct span i { display: block; height: 100%; border-radius: 99px; }

/* stock */
.gn-tabs2 { display: flex; gap: 0.3rem; padding: 0.6rem 1rem 0; border-bottom: 1px solid var(--cf-border); }
.gn-tabs2 button { border: 0; background: none; font-size: 0.8rem; font-weight: 600; color: var(--cf-muted); padding: 0.45rem 0.7rem; border-bottom: 2px solid transparent; cursor: pointer; display: flex; gap: 0.35rem; align-items: center; }
.gn-tabs2 button b { background: var(--cf-surface-alt); border-radius: 99px; padding: 0 0.45rem; font-size: 0.68rem; }
.gn-tabs2 button.gn-tabs2--on { color: rgb(var(--primary)); border-bottom-color: rgb(var(--primary)); }
.gn-tabs2 button.gn-tabs2--on b { background: rgb(var(--primary)); color: #fff; }
.gn-list { max-height: 360px; overflow: auto; }
.gn-stk { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 0.2rem 0.6rem; padding: 0.55rem 1rem; border-bottom: 1px solid var(--cf-surface-alt); align-items: center; }
.gn-stk:hover { background: var(--cf-surface); }
.gn-stk b { font-size: 0.82rem; color: var(--cf-ink-strong); }
.gn-stk small { font-size: 0.7rem; color: var(--cf-muted); }
.gn-lvl { display: flex; align-items: center; gap: 0.4rem; justify-content: flex-end; }
.gn-lvl__bar { width: 60px; height: 6px; border-radius: 99px; background: var(--cf-surface-alt); overflow: hidden; display: inline-block; }
.gn-lvl__bar i { display: block; height: 100%; border-radius: 99px; }
.gn-qty { font-weight: 800; font-variant-numeric: tabular-nums; font-size: 0.85rem; min-width: 2.5rem; text-align: right; }
.gn-badge { display: inline-block; font-size: 0.64rem; font-weight: 700; padding: 1px 7px; border-radius: 999px; }
.gn-badge--bad { background: #fef2f2; color: #dc2626; }
.gn-more { padding: 0.6rem 1rem; text-align: center; }

/* bénéfice */
.gn-benef-foot { display: flex; justify-content: space-between; gap: 0.5rem; flex-wrap: wrap; font-size: 0.75rem; color: var(--cf-muted); margin-top: 0.4rem; }
.gn-benef-foot b { color: var(--cf-ink-strong); }

/* mouvements */
.gn-lazy { display: flex; flex-direction: column; align-items: center; gap: 0.6rem; padding: 2rem 1rem; text-align: center; color: var(--cf-muted); font-size: 0.85rem; }
.gn-lazy > i { font-size: 2rem; color: var(--cf-faint); }
.gn-mvt-tools { display: flex; flex-wrap: wrap; gap: 0.5rem; padding: 0.75rem 1rem; border-bottom: 1px solid var(--cf-border); background: var(--cf-surface); }
.gn-in { color: #059669; font-weight: 700; }
.gn-out { color: #dc2626; font-weight: 700; }
.gn-dtype { display: inline-block; font-size: 0.66rem; font-weight: 700; padding: 1px 6px; border-radius: 5px; background: var(--cf-surface-alt); color: var(--cf-muted); }
.gn-pager { display: flex; justify-content: space-between; align-items: center; padding: 0.6rem 1rem; border-top: 1px solid var(--cf-border); font-size: 0.76rem; color: var(--cf-muted); }
.gn-pager__pg { display: inline-flex; gap: 0.4rem; align-items: center; }
.gn-pager__pg button { min-width: 1.9rem; height: 1.9rem; border: 1px solid var(--cf-border); background: #fff; border-radius: var(--cf-radius-xs); cursor: pointer; font-size: 0.76rem; }
.gn-pager__pg button:disabled { opacity: 0.4; cursor: default; }

/* états */
.gn-empty { text-align: center; padding: 2rem 1rem; color: var(--cf-muted); font-size: 0.82rem; }
.gn-empty i { font-size: 1.8rem; color: var(--cf-faint); display: block; margin-bottom: 0.3rem; }
.gn-skel { height: 0.75rem; border-radius: 6px; margin: 0.55rem 0; background: linear-gradient(90deg, #eef2f7 25%, #f8fafc 50%, #eef2f7 75%); background-size: 200% 100%; animation: gn-sh 1.3s infinite; }
.gn-skel--lg { height: 1.4rem; }
.gn-skel--chart { height: 280px; }
@keyframes gn-sh { to { background-position: -200% 0; } }

@media (max-width: 760px) {
    .gn-kpi__val { font-size: 1.1rem; }
    .gn-msearch input { width: 130px; }
    .gn-hide-sm { display: none; }
}

</style>
